import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import InsightsServices from "../../services/Insights.services";
import SqreemAIServices from "../../services/SqreemAI.services";
import { setReportBuilderMode } from "../../store/components/ReportBuilder.slice";
import { Tag } from "antd";
import { loadMarketCategoryReports, loadMarketCategoryReportsNotInState } from "../../utils/report/ReportsHelper";
import { fGetBehaviorMatchAndColor, getSelectedAnalysis } from "../../utils/oneintelligence/OneIntelligenceHelper";
import TopLevelCacheServices from "../../services/TopLevelCache.services";
import { capitalize, generateGUID } from "../../utils/MainHelper";
import { setLoadingResult, setLowscore, setMode, setPersonasParams, setResultMode } from "../../store/components/Minified.slice";
import useCategoryAnalysisTree from "../Tree/useCategoryAnalysisTree";
import WorkerServices from "../../services/Worker.services";
import TrackFeatureServices from "../../services/TrackFeature.services";
import { setMasterSize } from "../../store/components/Reports.slice";


function useGetMinifiedResult() {
    const dispatch = useDispatch()
    const [resultDataObj, setResultDataObj] = useState({ he: [], ai: [], all: [] });
    const [simpleSummaryValue, setSimpleSummaryValue] = useState("")
    const [percent, setPercent] = useState(0)
    const { isLoading, treeData } = useCategoryAnalysisTree(true, getSelectedAnalysis("marketcategory"));
    const { targets, negativeTargets } = useSelector((state) => state.minified)
    const { categoryDeepDiveData } = useSelector((state) => state.reports);
    const { configuration: { buildedTargets } } = useSelector((state) => state.marketExplorer);
    const [loadingNegative, setLoadingNegative] = useState(false)
    const [negativeList, setNegativeList] = useState([])
    const [monitorObj, setMonitorObj] = useState(TrackFeatureServices.getMonitor({ featureKey: "minifiedResult" }).commonParams)

    const [recommendedData, setRecommendedData] = useState([
        { key: "he" },
        { key: "ai" },
        { key: "all" }
    ])

    const onChangeAudienceName = (newData) => {
        setRecommendedData((state) => {
            return [...state].map((item) => {
                if (item.key === newData.key) {
                    return {
                        ...item,
                        data: {
                            ...item.data,
                            name: newData.name
                        }
                    }
                }
                return item
            })
        })
    }

    const replaceSummary = () => {
        let newSummary = [simpleSummaryValue]
        const newTargets = [...targets].sort(function (a, b) {
            return b.length - a.length;
        });
        const recursiveReplaceTarget = (targetIdx) => {
            const target = newTargets[targetIdx];
            if (!target) return
            const newTarget = <Tag className="oi-tag capitalize">{target}</Tag>

            let newSummaryList = []
            newSummary.forEach((sumText) => {
                let tempSummaryList = []
                if (typeof sumText === "string") {
                    if (sumText.split('"' + target + '"').length > 1) {
                        tempSummaryList = sumText.split('"' + target + '"')
                    } else if (sumText.split('"' + capitalize(target) + '"').length > 1) {
                        tempSummaryList = sumText.split('"' + capitalize(target) + '"')
                    } else if (sumText.split(target).length > 1) {
                        tempSummaryList = sumText.split(target)
                    } else if (sumText.split(capitalize(target)).length > 1) {
                        tempSummaryList = sumText.split(capitalize(target))
                    } else {
                        tempSummaryList.push(sumText)
                    }
                } else {
                    tempSummaryList.push(sumText)
                }

                tempSummaryList.forEach((text, idx) => {
                    newSummaryList.push(text)
                    if (idx !== tempSummaryList.length - 1) {
                        newSummaryList.push(newTarget)
                    }
                })
            })
            newSummary = newSummaryList
            recursiveReplaceTarget(targetIdx + 1)
        }
        recursiveReplaceTarget(0)
        return <>
            {newSummary.map((target) => {
                if (typeof sumText === "string") {
                    return <span>{target}</span>
                }
                return target
            })}
        </>
    }
    const getRecommendedItems = ({ all, ai, he }, genderAge) => {
        const recommendedItems = [
            { key: "he" },
            { key: "ai" },
            { key: "all" }
        ]
        const getSize = (list) => {
            return list.reduce((sum, item) => {
                sum += item.size
                return sum
            }, 0)
        }
        let targetAudience = ""
        if (all) {
            targetAudience = all[0].targetAudience
            recommendedItems[2].data = {
                name: "Upper Funnel",
                targetSize: getSize(all),
                targetAudience: targetAudience,
                list: all,
                genderAgeObj: genderAge.all
            }
        }
        if (he) {
            recommendedItems[0].data = {
                name: "Lower Funnel",
                targetSize: getSize(he),
                targetAudience: targetAudience,
                list: he,
                genderAgeObj: genderAge.he
            }
        }
        if (ai) {
            recommendedItems[1].data = {
                name: "Mid Funnel",
                targetSize: getSize(ai),
                targetAudience: targetAudience,
                list: ai,
                genderAgeObj: genderAge.ai
            }
        }

        setRecommendedData(recommendedItems)
    }

    const getAgeGenderDistribution = (list, type) => {
        const getCategoriesText = (itemList) => itemList.map((item) => item.discoveredCategory).join(", ")
        return TopLevelCacheServices.getMinifiedAgeGenderCache(type).then((cacheResp) => {
            if (cacheResp) return cacheResp
            return SqreemAIServices.getGenderAgeDistribution(getCategoriesText(list), monitorObj).then(async (resp) => {
                await TopLevelCacheServices.setMinifiedAgeGenderCache(resp, type)
                return resp
            })
        })
    }

    useEffect(() => {
        let timeout = null;
        if (!isLoading && buildedTargets.length && monitorObj) {
            timeout = setTimeout(() => {
                dispatch(setLoadingResult(true))
                setPercent(.01)
                let positivePercent = 0;
                let negativePercent = 0
                let hasNegative = false
                const changePercent = () => {
                    const newPercent = positivePercent + negativePercent
                    setPercent((prevpercent) => prevpercent > newPercent ? prevpercent : newPercent)
                }
                if (negativeTargets.length) {
                    hasNegative = true
                    setLoadingNegative(true)
                    TopLevelCacheServices.getMinifiedResultCache(true).then((resp) => {
                        const number = 49;
                        if (!resp) {
                            const negativeBuildTargets = [{
                                name: "Audience Name 1",
                                key: generateGUID(),
                                groups: [{
                                    text: 'Freeform',
                                    code: 'product_brand',
                                    packages: [
                                        'consumer',
                                        'pharma'
                                    ],
                                    bahavior: null,
                                    targets: negativeTargets.map((kw) => {
                                        return {
                                            name: kw,
                                            children: [
                                                {
                                                    name: kw
                                                }
                                            ]
                                        }
                                    })
                                }]
                            }]
                            loadMarketCategoryReportsNotInState(true, treeData, (obj) => {
                                const result = (obj.percent) * number;
                                negativePercent = result / 100
                                changePercent()
                            }, negativeBuildTargets).then((negativeListResp) => {
                                setNegativeList(negativeListResp);
                                setLoadingNegative(false)
                                TopLevelCacheServices.setMinifiedResultCache(negativeListResp, true);
                            })
                        } else {
                            setNegativeList(resp)
                            negativePercent = number / 100
                            changePercent()
                            setLoadingNegative(false)
                        }
                    })
                } else {
                    setNegativeList([])
                }

                TopLevelCacheServices.getMinifiedResultCache().then((resp) => {
                    const number = hasNegative ? 49 : 98;
                    if (!resp) {
                        loadMarketCategoryReports(true, treeData, (obj) => {
                            const result = (obj.percent) * number;
                            positivePercent = result / 100
                            changePercent()
                        }, monitorObj)
                    } else {
                        try {
                            dispatch(setMasterSize(resp[0].children[0].masterSize));
                        } catch (error) { }
                        positivePercent = number / 100
                        changePercent()
                    }
                })
            }, 300)
        }
        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, [isLoading, treeData, negativeTargets, buildedTargets, monitorObj])

    useEffect(() => {
        let timeout = null;
        if (percent == .98 && !loadingNegative && categoryDeepDiveData.length) {
            timeout = setTimeout(() => {
                setSimpleSummaryValue("")
                TopLevelCacheServices.setMinifiedResultCache(categoryDeepDiveData);
                WorkerServices.parseMinifiedFreeformResult([categoryDeepDiveData, negativeList]).then((parsedResp) => {
                    if (!parsedResp.he.length && !parsedResp.ai.length) {
                        dispatch(setLowscore(true))
                        setPercent(1)
                        dispatch(setMode("creation"))
                        dispatch(setReportBuilderMode("marketcategory"));
                        dispatch(setResultMode("dashboard"));
                        dispatch(setPersonasParams(null));
                        dispatch(setLoadingResult(false))
                    } else {
                        dispatch(setLowscore(false))
                        const addBehaviorMatch = (list) => list.map((item) => ({
                            ...item,
                            children: item.children.map((child) => ({
                                ...child,
                                ...fGetBehaviorMatchAndColor(child.score)
                            }))
                        }))
                        const newResp = {
                            he: addBehaviorMatch(parsedResp.he),
                            ai: addBehaviorMatch(parsedResp.ai),
                            all: addBehaviorMatch(parsedResp.all)
                        }

                        setResultDataObj(newResp)

                        const ageGenderObj = { ai: null, he: null, all: null }
                        const add5percent = () => {
                            setPercent((prcnt) => prcnt + .005)
                        }

                        const { startMonitor, endMonitor } = TrackFeatureServices.getMonitor(monitorObj)
                        let monitorUrl = ""
                        const callBackGetUrl = (params, url) => {
                            monitorUrl = url
                            startMonitor(params, { url: monitorUrl })
                        }
                        Promise.all([
                            InsightsServices.runSimpleSummary(targets, newResp.all, false, "all", callBackGetUrl).then((resp) => {
                                add5percent();
                                endMonitor(resp, { url: monitorUrl })
                                return resp
                            }),
                            getAgeGenderDistribution(newResp.ai, "ai", monitorObj).then((resp) => {
                                ageGenderObj.ai = resp
                                // add5percent();
                            }),
                            getAgeGenderDistribution(newResp.he, "he", monitorObj).then((resp) => {
                                ageGenderObj.he = resp
                                // add5percent();
                            }),
                            getAgeGenderDistribution(newResp.all, "all", monitorObj).then((resp) => {
                                ageGenderObj.all = resp
                                // add5percent();
                            }),
                        ]).then((responses) => {
                            getRecommendedItems(newResp, ageGenderObj)
                            setSimpleSummaryValue(responses[0].data[0]);
                            dispatch(setReportBuilderMode("marketcategory"));
                            setPercent(1)
                            dispatch(setLoadingResult(false))
                        })
                    }
                })
            }, 1000);
        }
        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, [percent, loadingNegative, categoryDeepDiveData, negativeList, monitorObj])

    return {
        simpleSummaryValue,
        resultDataObj,
        percent,
        summary: replaceSummary(),
        recommendedData,
        onChangeAudienceName
    };
}

export default useGetMinifiedResult;
